'use client';
import useSWR from 'swr';

import { useContext, useEffect, useMemo } from 'react';
import { type UseUser, useUser } from '~/hooks/store/user';
import { fetchOrderId } from '~/src/services/arcApi';
import { isActiveToken } from '~/src/services/users';
import { RootContext } from '~/store';
import { OrderOverview } from '~/types/resources/orderOverview';

type UseOrderIdInfoReturn = {
  order: OrderOverview | undefined;
  loading: boolean;
  error: Error;
  mutate: () => void;
};

type UseOrderIdInfo = (order_id?: string) => UseOrderIdInfoReturn;

type IfUseOrderIdInfo = (useUser: UseUser) => UseOrderIdInfo;
export const ifUseOrderIdInfo: IfUseOrderIdInfo =
  (): UseOrderIdInfo => (order_id?) => {
    const { token } = useContext(RootContext);

    const fetchParams = useMemo(() => {
      if (token == null) return null;
      if (order_id == null) return null;
      if (!isActiveToken(token)) return null;
      return [order_id, token.token];
    }, [token, order_id]);
    const {
      data,
      error: _error,
      mutate,
    } = useSWR(fetchParams, fetchOrderId, {
      headers: {
        Authorization: `Bearer example`,
      },
      enabled: order_id != null,
      isPaused: () => false,
      dedupingInterval: 1000 * 60 * 5,
      revalidateIfStale: false,
    });

    const error = useMemo(() => {
      if (_error != null) return error;
      if (data?.statusCode != 200) return data?.body;
      return undefined;
    }, [_error, data]);

    useEffect(() => {
      if (token != null && !isActiveToken(token)) return;
      mutate();
    }, [token]);

    return {
      order: data?.body ?? undefined,
      loading: !data && !error,
      error: error,
      mutate,
    };
  };

export const useOrderIdInfo = ifUseOrderIdInfo(useUser);
